// Pure data transformations for the Releases page: version maths, overdue
// derivation, month-grid building, drag-drop validation, and KPI counters.
// No side effects, no API calls — keeps the slice and components thin.

import dayjs, { Dayjs } from "dayjs";
import {
	BehindSeverity,
	DropVerdict,
	IMonthCell,
	IRailTenant,
	IRelease,
	IReleaseTenantMeta,
	IReleaseVersions,
	ReleaseDisplayStatus,
} from "../models/interfaces";

// ── Bundle-version maths ──────────────────────────────────────────────────────

export type VersionTuple = [number, number, number];

// Deployment-pipeline bundle ids carry a "p-" env prefix (e.g. "p-3.7.4") —
// strip it before any parsing/display, like the Tenants page does.
export const stripBundlePrefix = (raw: string): string => raw.trim().replace(/^p-/i, "");

// Parse "MAJOR.MINOR.PATCH" (leading "p-" env prefix and/or "v"/"V" are
// tolerated). Anything else — empty, non-string, "3.2", "3.2.21.1", "v514" —
// returns null and is treated as the OLDEST possible version everywhere
// downstream. Never throws. (2026-09-09: the missing p- strip made EVERY
// fleet bundle parse as malformed, so the whole rail showed "1 behind".)
export const parseVersion = (raw: unknown): VersionTuple | null => {
	if (typeof raw !== "string") return null;
	const match = stripBundlePrefix(raw).replace(/^v/i, "").match(/^(\d+)\.(\d+)\.(\d+)$/);
	if (!match) return null;
	return [Number(match[1]), Number(match[2]), Number(match[3])];
};

// Numeric tuple compare (so "3.2.9" < "3.2.21", unlike a string compare).
// Malformed versions sort before everything; two malformed compare equal.
export const compareVersions = (a: unknown, b: unknown): number => {
	const pa = parseVersion(a);
	const pb = parseVersion(b);
	if (!pa && !pb) return 0;
	if (!pa) return -1;
	if (!pb) return 1;
	for (let i = 0; i < 3; i++) {
		if (pa[i] !== pb[i]) return pa[i] < pb[i] ? -1 : 1;
	}
	return 0;
};

// Severity of being behind the latest bundle. A tenant reporting a version
// NEWER than latest (secret race while TeamCity rolls the fleet) counts as
// up to date — never "negative behind".
export const behindSeverity = (current: unknown, latest: unknown): BehindSeverity => {
	if (compareVersions(current, latest) >= 0) return "latest";
	const cur = parseVersion(current);
	const lat = parseVersion(latest);
	if (!cur || !lat) return "red"; // malformed current = oldest possible
	if (cur[0] === lat[0] && cur[1] === lat[1]) return lat[2] - cur[2] >= 2 ? "red" : "amber";
	return "red"; // older MAJOR.MINOR line
};

// Known bundle list, oldest → newest, derived from the versions payload +
// latest_bundle (per the frozen contract — no other source). Normalised
// (p- prefix stripped) and deduped on the normalised id, so "p-3.7.5" and
// "3.7.5" are ONE bundle — a duplicate here would inflate every behind-count.
export const knownBundles = (versions: IReleaseVersions | null | undefined): string[] => {
	if (!versions) return [];
	const set = new Set<string>();
	if (versions.latest_bundle) set.add(stripBundlePrefix(versions.latest_bundle));
	Object.values(versions.tenants ?? {}).forEach((tenant) => {
		if (tenant?.bundle_version) set.add(stripBundlePrefix(tenant.bundle_version));
	});
	return Array.from(set).sort(compareVersions);
};

// "N behind" = how many known bundles are strictly newer than the current one.
export const behindCount = (current: unknown, bundles: string[], latest: unknown): number => {
	if (compareVersions(current, latest) >= 0) return 0;
	return bundles.filter((bundle) => compareVersions(bundle, current) > 0).length;
};

// Target-bundle options for the Plan modal: known bundles strictly newer than
// the tenant's current one; a tenant already on latest can still re-plan latest.
export const targetBundleOptions = (current: string | null, versions: IReleaseVersions | null): string[] => {
	const newer = knownBundles(versions).filter((bundle) => compareVersions(bundle, current) > 0);
	if (newer.length) return newer;
	return versions?.latest_bundle ? [versions.latest_bundle] : [];
};

// ── Dates & overdue derivation ────────────────────────────────────────────────

export const todayIso = (): string => dayjs().startOf("day").format("YYYY-MM-DD");

// Overdue is DERIVED: still planned and the date has passed. A release planned
// for today is NOT overdue. ISO strings compare correctly lexicographically.
export const isOverdue = (release: IRelease, today: string): boolean =>
	release?.status === "planned" && !!release.planned_date && release.planned_date < today;

export const displayStatus = (release: IRelease, today: string): ReleaseDisplayStatus => {
	if (release?.status === "completed") return "completed";
	return isOverdue(release, today) ? "overdue" : "planned";
};

// Chip/badge copy + shared KCC badge class + Material Symbols icon per status.
export const RELEASE_STATUS_META: Record<ReleaseDisplayStatus, { label: string; badgeCls: string; chipCls: string; icon: string }> = {
	completed: { label: "Released", badgeCls: "kcc-badge--active", chipCls: "releases__chip--done", icon: "check_circle" },
	planned: { label: "Planned", badgeCls: "kcc-badge--suspended", chipCls: "releases__chip--planned", icon: "schedule" },
	overdue: { label: "Overdue", badgeCls: "kcc-badge--decommissioned", chipCls: "releases__chip--overdue", icon: "error" },
};

// "Aug 12, 2026"; falls back to the raw value when unparseable.
export const formatDate = (iso: string | null | undefined): string => {
	if (!iso) return "—";
	const date = dayjs(iso);
	return date.isValid() ? date.format("MMM D, YYYY") : iso;
};

// Short day label: the year is dropped when it matches the current year.
export const formatDayLabel = (iso: string, now: Dayjs = dayjs()): string => {
	const date = dayjs(iso);
	if (!date.isValid()) return iso ?? "";
	return date.year() === now.year() ? date.format("MMM D") : date.format("MMM D, YYYY");
};

// ── Month grid ───────────────────────────────────────────────────────────────

// 42 cells (6 weeks) starting on the Sunday on/before the 1st. Out-of-month
// cells keep their REAL dateIso so drops on leading/trailing days still work.
export const buildMonthGrid = (month: Dayjs): IMonthCell[] => {
	const first = month.startOf("month");
	const start = first.subtract(first.day(), "day");
	return Array.from({ length: 42 }, (_, i) => {
		const date = start.add(i, "day");
		return {
			dateIso: date.format("YYYY-MM-DD"),
			dayOfMonth: date.date(),
			inMonth: date.month() === first.month() && date.year() === first.year(),
		};
	});
};

// Group releases by planned day (cancelled excluded). Built once per releases
// change and memoized by the caller so drag-over repaints don't re-scan.
export const groupReleasesByDay = (releases: IRelease[]): Map<string, IRelease[]> => {
	const map = new Map<string, IRelease[]>();
	(releases ?? []).forEach((release) => {
		if (!release || release.status === "cancelled" || !release.planned_date) return;
		const list = map.get(release.planned_date);
		if (list) list.push(release);
		else map.set(release.planned_date, [release]);
	});
	return map;
};

// Busy days: cap the chips and fold the rest behind a "+N more" toggle.
export const DAY_CHIP_CAP = 4;

export const partitionDayChips = (
	dayReleases: IRelease[],
	expanded: boolean,
	cap: number = DAY_CHIP_CAP,
): { visible: IRelease[]; hiddenCount: number } => {
	const list = dayReleases ?? [];
	if (expanded || list.length <= cap) return { visible: list, hiddenCount: 0 };
	const visible = list.slice(0, cap - 1); // leave one row for the "+N more" toggle
	return { visible, hiddenCount: list.length - visible.length };
};

// ── Drag-drop validation ─────────────────────────────────────────────────────

// The one OPEN planned release for a tenant (business rule: max one).
export const openPlannedRelease = (releases: IRelease[], tenantId: string): IRelease | undefined =>
	(releases ?? []).find((release) => release?.tenant_id === tenantId && release.status === "planned");

// Past dates are ALLOWED: operators may backfill an already-shipped release onto
// its real (past) date. `today` is kept in the signature for callers/compat.
export const validateTenantDrop = (releases: IRelease[], tenantId: string, dateIso: string, today: string): DropVerdict => {
	void dateIso;
	void today;
	const open = openPlannedRelease(releases, tenantId);
	if (open) return { ok: false, reason: "openPlanExists", existingDate: open.planned_date };
	return { ok: true };
};

export const validateReleaseDrop = (releases: IRelease[], id: string, dateIso: string, today: string): DropVerdict => {
	void today;
	const release = (releases ?? []).find((rel) => rel?.id === id);
	if (!release || release.status !== "planned") return { ok: false, reason: "notDraggable" };
	if (release.planned_date === dateIso) return { ok: false, reason: "sameDay" };
	return { ok: true };
};

// ── Tenant rail ──────────────────────────────────────────────────────────────

// Rail cards derive from the versions payload (the secret is the source of
// truth for which tenants exist on the release plane), enriched with names and
// types from the tenants list / release records.
export const buildRailTenants = (
	versions: IReleaseVersions | null,
	tenantsMeta: Record<string, IReleaseTenantMeta>,
	releases: IRelease[],
): IRailTenant[] => {
	if (!versions?.tenants) return [];
	const bundles = knownBundles(versions);
	return Object.entries(versions.tenants)
		.map(([id, entry]) => {
			const meta = tenantsMeta?.[id];
			const current = entry?.bundle_version ?? null;
			const open = openPlannedRelease(releases, id);
			return {
				id,
				code: meta?.code || id,
				name: meta?.name || meta?.code || id,
				type: meta?.type ?? null,
				bundleVersion: current ? stripBundlePrefix(current) : null,
				severity: behindSeverity(current, versions.latest_bundle),
				// A count only makes sense for a comparable version — a legacy id
				// (v514) isn't "N behind", it's off the versioning scheme entirely
				// (behind: 0 + severity red renders as the "legacy" pill).
				behind: parseVersion(current) ? behindCount(current, bundles, versions.latest_bundle) : 0,
				plannedDate: open?.planned_date ?? null,
			};
		})
		.sort((a, b) => a.name.localeCompare(b.name));
};

export const filterRailTenants = (tenants: IRailTenant[], search: string): IRailTenant[] => {
	const term = (search ?? "").trim().toLowerCase();
	if (!term) return tenants;
	return tenants.filter(
		(tenant) => tenant.name.toLowerCase().includes(term) || tenant.code.toLowerCase().includes(term),
	);
};

// ── History drawer ───────────────────────────────────────────────────────────

// All non-cancelled releases, newest planned date first.
export const sortReleasesDesc = (releases: IRelease[]): IRelease[] =>
	[...(releases ?? [])]
		.filter((release) => release && release.status !== "cancelled")
		.sort((a, b) => (b.planned_date ?? "").localeCompare(a.planned_date ?? ""));

export const filterHistory = (
	releases: IRelease[],
	search: string,
	tenantsMeta: Record<string, IReleaseTenantMeta>,
): IRelease[] => {
	const term = (search ?? "").trim().toLowerCase();
	if (!term) return releases;
	return releases.filter((release) => {
		const name = tenantsMeta?.[release.tenant_id]?.name || release.tenant_name || "";
		return (
			release.tenant_code.toLowerCase().includes(term) ||
			name.toLowerCase().includes(term) ||
			(release.to_version ?? "").toLowerCase().includes(term) ||
			(release.from_version ?? "").toLowerCase().includes(term)
		);
	});
};

// ── Tenant meta enrichment ───────────────────────────────────────────────────

// Build the immutable id → {code, name, type} lookup from GET /tenants,
// back-filling names from release records when needed.
export const buildTenantMeta = (
	tenants: { id?: string; tenant_code?: string; tenant_name?: string; tenant_type?: string }[] | null | undefined,
	releases?: IRelease[],
): Record<string, IReleaseTenantMeta> => {
	const meta: Record<string, IReleaseTenantMeta> = {};
	(tenants ?? []).forEach((tenant) => {
		if (!tenant?.id || !tenant?.tenant_code) return;
		meta[tenant.id] = { code: tenant.tenant_code, name: tenant.tenant_name || tenant.tenant_code, type: tenant.tenant_type ?? null };
	});
	(releases ?? []).forEach((release) => {
		if (!release?.tenant_id || meta[release.tenant_id]) return;
		meta[release.tenant_id] = { code: release.tenant_code, name: release.tenant_name || release.tenant_code, type: null };
	});
	return meta;
};
